// useBroadcastManager.ts
import React, { useCallback, useEffect, useRef, useState } from "react";
import IVSBroadcastClient from "amazon-ivs-web-broadcast";
import { toast } from "../@/components/ui/use-toast";
import { formatDateTime, runLoadingProgress } from "../lib/utils";
import { STREAM_STATUS } from "../lib/constants";
import { IData } from "../lib/interfaces";
import { useOutletContext } from "react-router-dom";

/* ============================================
   Utility Functions
============================================ */
// Determine if a given camera label refers to a front camera.
const isFrontCamera = (label: string): boolean => {
  const lowerLabel = label.toLowerCase();
  const frontKeywords = [
    "front",
    "selfie",
    "face",
    "integrated",
    "built-in",
    "internal",
  ];
  const backKeywords = [
    "back",
    "environment",
    "rear",
    "usb",
    "external",
    "webcam",
  ];
  if (frontKeywords.some((keyword) => lowerLabel.includes(keyword))) {
    return true;
  }
  return !backKeywords.some((keyword) => lowerLabel.includes(keyword));
};

// Get video constraints based on deviceId (and front/back camera)
const getVideoConstraints = (
  deviceId?: string,
  deviceLabel?: string
): MediaTrackConstraints => {
  const isFront = deviceLabel ? isFrontCamera(deviceLabel) : true;
  const constraints: MediaTrackConstraints = {
    width: { min: 640, ideal: 1280, max: 1920 },
    height: { min: 480, ideal: 720, max: 1080 },
    aspectRatio: { ideal: 16 / 9 },
  };

  if (deviceId) {
    constraints.deviceId = { exact: deviceId };
  }
  if (isFront) {
    constraints.facingMode = "user";
    constraints.width = { min: 640, ideal: 1280, max: 1280 };
    constraints.height = { min: 480, ideal: 720, max: 720 };
  } else {
    constraints.facingMode = "environment";
  }
  return constraints;
};

const createMirroredStreamComposite = async (
  mediaStream: MediaStream
): Promise<MediaStream> => {
  const isMobile = /iPhone|iPad|iPod|android/i.test(navigator.userAgent);
  const videoTrack = mediaStream.getVideoTracks()[0];
  const videoSettings = videoTrack.getSettings();
  const width = isMobile ? 720 : videoSettings.width || 1280;
  const height = isMobile ? 1280 : videoSettings.height || 720;

  const compositeCanvas = document.createElement("canvas");
  compositeCanvas.width = width;
  compositeCanvas.height = height;
  const ctx = compositeCanvas.getContext("2d");

  const video = document.createElement("video");
  video.srcObject = mediaStream;
  video.muted = true;

  // Wait for video to load metadata and start playing.
  await new Promise<void>((resolve) => {
    video.onloadedmetadata = () => {
      video.play().then(resolve);
    };
  });

  // Function to draw a flipped frame onto the canvas.
  const drawFrame = () => {
    if (ctx) {
      ctx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);
      ctx.save();
      // Flip horizontally.
      ctx.scale(-1, 1);
      // Draw the video such that it appears mirrored.
      ctx.drawImage(
        video,
        -compositeCanvas.width,
        0,
        compositeCanvas.width,
        compositeCanvas.height
      );
      ctx.restore();
    }
  };

  // Update the canvas at ~30fps.
  // Optionally, store the interval ID on the canvas for cleanup.
  (compositeCanvas as any)._updateInterval = setInterval(drawFrame, 1000 / 30);

  // Initial frame draw.
  drawFrame();

  return compositeCanvas.captureStream(30);
};

/* ============================================
   Hook Definition
============================================ */
export interface BroadcastManagerProps {
  videoRef: React.RefObject<HTMLCanvasElement>;
  updateLiveSession: any;
  selectedData: IData | null;
  setSelectedData: React.Dispatch<React.SetStateAction<IData | null>>;
  handleSessionUpdated: () => void;
  setLoading: (loading: boolean) => void;
  setIsLive: (isLive: boolean) => void;
  setUpdating: (updating: boolean) => void;
}

interface LayoutContext {
  showAlertAction: boolean;
  setShowAlertAction: React.Dispatch<React.SetStateAction<boolean>>;
  showAlertOnRouteChange: boolean;
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useBroadcastManager = ({
  videoRef,
  updateLiveSession,
  selectedData,
  setSelectedData,
  handleSessionUpdated,
  setLoading,
  setIsLive,
  setUpdating,
}: BroadcastManagerProps) => {
  const isMobileViewScreen = window.innerWidth < 768;
  // State declarations
  const [micOn, setMicOn] = useState<boolean>(true);
  const [cameraEnabled, setCameraEnabled] = useState<boolean>(true);
  const [screenSharing, setScreenSharing] = useState<boolean>(false);
  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [broadcaster, setBroadcaster] = useState<any>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [hasVideoPermission, setHasVideoPermission] = useState<boolean | null>(
    null
  );
  const [hasAudioPermission, setHasAudioPermission] = useState<boolean | null>(
    null
  );
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [streamStatus, setStreamStatus] = useState(false);
  const {
    showAlertAction,
    setShowAlertAction,
    setShowAlertOnRouteChange,
    showAlertOnRouteChange,
  } = useOutletContext<LayoutContext>();
  const screenUpdateIntervalRef = useRef<number | null>(null);
  const parentNodeRef = useRef<Node | null>(null);

  /* ============================================
       Do not leave without killing current seesion
    ============================================ */

  useEffect(() => {
    if (selectedData && broadcaster && !showAlertOnRouteChange) {
      setShowAlertOnRouteChange(true);
    }
  }, [broadcaster, selectedData, showAlertOnRouteChange]);

  /* ============================================
     Device and Permission Helpers
     ============================================ */

  const getCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );

      let frontCamera = videoDevices.find((device) =>
        isFrontCamera(device.label)
      );
      let backCamera = videoDevices.find(
        (device) => !isFrontCamera(device.label)
      );

      // Fallback if we can't determine camera types
      if (!frontCamera || !backCamera) {
        frontCamera = videoDevices[0];
        backCamera = videoDevices[1];
      }

      const mainCameras = [frontCamera, backCamera].filter(
        Boolean
      ) as MediaDeviceInfo[];
      setCameras(mainCameras);
    } catch (error) {
      console.error("Error getting cameras:", error);
    }
  }, []);

  const checkMediaPermissions = useCallback(async () => {
    let permissions = {
      video: false,
      audio: false,
    };

    try {
      // Try to get media stream to check actual permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      // Check which permissions were granted
      permissions = {
        video: stream.getVideoTracks().length > 0,
        audio: stream.getAudioTracks().length > 0,
      };

      // Clean up the test stream
      stream.getTracks().forEach((track) => track.stop());

      // Update permission states
      setHasVideoPermission(permissions.video);
      setHasAudioPermission(permissions.audio);
    } catch (error: unknown) {
      // Handle errors with type safety
      const err = error as Error;
      console.error("Error checking media permissions:", err.message);
      setHasVideoPermission(false);
      setHasAudioPermission(false);
    }
  }, []);

  /* ============================================
      Stream Setup Helpers
    ============================================ */
  // Update Tawk.to widget visibility based on live state.
  const updateTawkVisibility = useCallback((isLiveState: boolean) => {
    try {
      const tawkAPI = (window as any).Tawk_API;
      if (tawkAPI?.hideWidget && tawkAPI?.showWidget) {
        if (isLiveState) {
          tawkAPI.setAttributes?.({}, (error: any) => {
            if (error) console.warn("Tawk.to setAttributes warning:", error);
          });
          tawkAPI.hideWidget();
        } else {
          tawkAPI.showWidget();
        }
      }
    } catch (error) {
      console.warn("Error updating Tawk visibility:", error);
    }
  }, []);

  // Check Stream Status.
  const checkStreamStatus = async (session: IData) => {
    if (!session.liveSessionUrl) return false;

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const fetchStatus = async () => {
      try {
        const response = await fetch(session.liveSessionUrl);
        return response.ok;
      } catch (error) {
        console.error("Error checking stream status:", error);
        return false;
      }
    };

    // First attempt
    const firstAttempt = await fetchStatus();

    // If the first attempt fails, return immediately without setting status
    if (!firstAttempt) {
      setStreamStatus(false);
      return false;
    }

    // Wait before the second attempt
    await delay(3000);

    // Second attempt
    const secondAttempt = await fetchStatus();

    // Set status based on the second attempt
    setStreamStatus(secondAttempt);

    return secondAttempt;
  };




  // Update live session status on the server.
  const updateStatus = async (data: IData) => {
    try {
      await updateLiveSession({
        updateLiveSessionDto: {
          id: data.id,
          statusId: STREAM_STATUS.STREAM_STARTED,
        },
      }).unwrap();
      await handleSessionUpdated();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  /* ============================================
      Broadcast Control Functions
    ============================================ */
  const startBroadcast = useCallback(
    async (data: IData) => {
      let localMediaStream: MediaStream | null = null;
      let broadcastClient: any = null;
      const isStreamActive = await checkStreamStatus(data);
      if (isStreamActive) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "This session is currently being streamed.",
          showIcon: true,
        });
        return;
      }
      // Clean up any existing streams.
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (!broadcaster && data.ingestUrl && data.streamKey) {
        try {
          setIsLive(true);
          updateTawkVisibility(true);
          setSelectedData(data);
          setLoadingProgress(1);
          setLoadingMessage("Initializing stream...");

          const loadingSteps = [
            { start: 1, end: 20, delay: 30 },
            { start: 21, end: 40, delay: 20 },
            { start: 41, end: 60, delay: 20 },
            { start: 61, end: 80, delay: 15 },
            { start: 81, end: 99, delay: 15 },
          ];
          await runLoadingProgress(setLoadingProgress, loadingSteps);

          try {
            setLoadingMessage("Requesting camera access...");
            localMediaStream = await navigator.mediaDevices.getUserMedia({
              video: getVideoConstraints(),
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
            });

            // Verify stream is still active before proceeding
            if (!localMediaStream?.active) {
              new Error("Media stream became inactive");
            }

            const vidTrack = localMediaStream.getVideoTracks()[0];
            const audioTrack = localMediaStream.getAudioTracks()[0];
            setHasVideoPermission(true);
            setHasAudioPermission(true);

            setLoadingMessage("Setting up broadcast...");
            const ingestEndpoint = data.ingestUrl.replace("rtmps:", "");
            broadcastClient = IVSBroadcastClient.create({
              streamConfig: isMobileViewScreen
                ? IVSBroadcastClient.BASIC_PORTRAIT
                : IVSBroadcastClient.BASIC_LANDSCAPE,
              ingestEndpoint,
            });

            // Verify video track is still enabled before adding to broadcaster
            if (vidTrack && vidTrack.enabled) {
              const isBack = !isFrontCamera(vidTrack.label);
              if (!isBack) {
                const mirroredStream = await createMirroredStreamComposite(
                  localMediaStream
                );
                await broadcastClient.addVideoInputDevice(
                  mirroredStream,
                  "camera",
                  { index: 0 }
                );
              } else {
                await broadcastClient.addVideoInputDevice(
                  new MediaStream([vidTrack]),
                  "camera",
                  { index: 0 }
                );
              }
              setVideoTrack(vidTrack);
              setCameraEnabled(true);
            }

            if (audioTrack && audioTrack.enabled) {
              await broadcastClient.addAudioInputDevice(
                new MediaStream([audioTrack]),
                "microphone"
              );
              setMicOn(true);
            }

            if (videoRef.current) {
              broadcastClient.attachPreview(videoRef.current);
            }

            setStream(localMediaStream);
            setBroadcaster(broadcastClient);

            setLoadingMessage("Connecting to server...");
            await broadcastClient.startBroadcast(data.streamKey);

            // Only update status after broadcast has successfully started
            setLoadingMessage("Finalizing setup...");

            if (isMobileViewScreen) {
              await getCameras();
            }

            // Final check to ensure everything is still active
            if (!localMediaStream?.active || !vidTrack?.enabled) {
              new Error("Stream or video track became inactive");
            }

            setLoadingProgress(100);
            setLoadingMessage("Stream started successfully!");

            setTimeout(() => {
              setLoadingProgress(0);
              setLoadingMessage("");
            }, 500);
            await updateStatus(data);
          } catch (error) {
            console.error("Error getting initial permissions:", error);
            // Fallback: create broadcaster without media.
            const ingestEndpoint = data.ingestUrl.replace("rtmps:", "");
            const client = IVSBroadcastClient.create({
              streamConfig: isMobileViewScreen
                ? IVSBroadcastClient.BASIC_PORTRAIT
                : IVSBroadcastClient.BASIC_LANDSCAPE,
              ingestEndpoint,
            });
            if (videoRef.current) client.attachPreview(videoRef.current);
            setBroadcaster(client);
            setLoadingMessage("Connecting to server...");
            await client.startBroadcast(data.streamKey);
            setCameraEnabled(false);
            setMicOn(false);
            setStream(null);
            setLoadingMessage("Finalizing setup...");
            await updateStatus(data);
            setLoadingProgress(100);
            setLoadingMessage("Stream started successfully!");
            setTimeout(() => {
              setLoadingProgress(0);
              setLoadingMessage("");
            }, 500);
          }
        } catch (error) {
          console.error("Error starting broadcast:", error);
          setSelectedData(null);
          setLoadingProgress(0);
          setLoadingMessage("");
          setIsLive(false);
          updateTawkVisibility(false);
          setBroadcaster(null);
          setStream(null);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to start broadcast",
            showIcon: true,
          });
          return;
        } finally {
          if (broadcastClient) {
            toast({
              variant: "success",
              title: "Success",
              description: "Live session started",
              showIcon: true,
            });
          }
        }
      }
    },
    [
      broadcaster,
      stream,
      isMobileViewScreen,
      setIsLive,
      setSelectedData,
      updateTawkVisibility,
      getCameras,
      updateStatus,
      videoRef,
      setShowAlertOnRouteChange,
    ]
  );

  const stopBroadcast = useCallback(async () => {
    if (!selectedData?.id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No active session found",
        showIcon: true,
      });
      return;
    }

    try {
      setLoading(true);
      // Update session status
      const response = await updateLiveSession({
        updateLiveSessionDto: {
          endDateTime: formatDateTime(new Date(), "YYYY-MM-DDTHH:mm:ssZ"),
          id: selectedData.id,
          statusId: STREAM_STATUS.STREAM_ENDED,
        },
      }).unwrap();

      // Clean up all resources
      cleanupStreams();
      cleanupBroadcaster();

      // Clear video preview
      if (videoRef.current) {
        const ctx = videoRef.current.getContext("2d");
        ctx?.clearRect(0, 0, videoRef.current.width, videoRef.current.height);
      }

      // Reset states
      setMicOn(false);
      setCameraEnabled(false);
      setVideoTrack(null);
      setShowAlertOnRouteChange(false);
      setSelectedData(null);

      // Handle Safari-specific cleanup
      if (
        /Safari/.test(navigator.userAgent) &&
        !/Chrome/.test(navigator.userAgent)
      ) {
        try {
          const emptyStream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: false,
          });
          emptyStream.getTracks().forEach((track) => track.stop());
        } catch (err) {
          console.warn("Safari: Unable to release camera:", err);
        }
      }

      // Show success message if session was updated successfully
      if (response?.message?.includes("Live session updated successfully")) {
        toast({
          variant: "success",
          title: "Success",
          description: "Live session ended",
          showIcon: true,
        });
        await handleSessionUpdated();
      }
    } catch (error) {
      console.error("Error stopping broadcast:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to stop broadcast. Please try again.",
        showIcon: true,
      });
    } finally {
      setLoading(false);
      setIsLive(false);
      updateTawkVisibility(false);
    }
  }, [
    broadcaster,
    stream,
    selectedData,
    updateLiveSession,
    handleSessionUpdated,
    setLoading,
    setShowAlertOnRouteChange,
    updateTawkVisibility,
    screenSharing,
    videoRef,
    setSelectedData,
    setIsLive,
  ]);

  const cleanupStreams = () => {
    if (broadcaster) {
      try {
        if (stream) {
          stream.getTracks().forEach((track) => {
            track.stop();
            stream.removeTrack(track);
          });
          setStream(null);
        }

        if (screenStream) {
          screenStream.getTracks().forEach((track) => {
            track.stop();
            screenStream.removeTrack(track);
          });
          setScreenStream(null);
        }

        if (screenSharing && broadcaster?.getVideoInputDevice("screen")) {
          setScreenSharing(false);
          broadcaster.removeVideoInputDevice("screen");
        }
      } catch (e) {}
    }
  };

  // Clean up broadcaster
  const cleanupBroadcaster = () => {
    if (broadcaster) {
      try {
        broadcaster.stopBroadcast();

        // Clean up audio/video inputs
        const audioInput = broadcaster.getAudioInputDevice();
        const videoInput = broadcaster.getVideoInputDevice();

        if (audioInput?.track) {
          audioInput.track.stop();
          broadcaster.removeAudioInputDevice("microphone");
        }

        if (videoInput?.track) {
          videoInput.track.stop();
          broadcaster.removeVideoInputDevice("camera");
        }

        setBroadcaster(null);
      } catch (err) {
        console.warn("Error cleaning up broadcaster:", err);
      }
    }
  };

  const toggleCameraScreenShare = useCallback(async () => {
    if (!broadcaster || !videoRef.current) return;
    try {
      setUpdating(true);
      // Get the parent reference
      const parent = (parentNodeRef.current ||
        videoRef.current.parentNode) as Element;
      if (!parent) return;

      if (cameraEnabled) {
        // Disable camera but keep screen share
        if (stream) {
          stream.getVideoTracks().forEach((track) => {
            track.stop();
            track.enabled = false;
            stream.removeTrack(track);
          });
        }
        if (videoTrack) {
          videoTrack.stop();
          videoTrack.enabled = false;
        }
        if (broadcaster.getVideoInputDevice("camera")) {
          await broadcaster.removeVideoInputDevice("camera");
        }
        // Update screen share without PIP
        if (screenStream) {
          const screenTrack = screenStream.getVideoTracks()[0];
          const screenVideo = document.createElement("video");
          screenVideo.srcObject = new MediaStream([screenTrack]);
          screenVideo.autoplay = true;
          screenVideo.playsInline = true;
          screenVideo.muted = true;

          await new Promise<void>((resolve) => {
            screenVideo.onloadedmetadata = () =>
              screenVideo.play().then(resolve);
          });

          const compositeCanvas = document.createElement("canvas");
          compositeCanvas.width = 1920;
          compositeCanvas.height = 1080;
          const ctx = compositeCanvas.getContext("2d");
          if (!ctx) return;

          const drawComposite = () => {
            if (document.hidden) return;
            ctx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);
            ctx.fillStyle = "#000000";
            ctx.fillRect(0, 0, compositeCanvas.width, compositeCanvas.height);

            // Calculate scaling to maintain aspect ratio
            const scale = Math.min(
              compositeCanvas.width / screenVideo.videoWidth,
              compositeCanvas.height / screenVideo.videoHeight
            );

            const width = screenVideo.videoWidth * scale;
            const height = screenVideo.videoHeight * scale;
            const x = (compositeCanvas.width - width) / 2;
            const y = (compositeCanvas.height - height) / 2;

            ctx.drawImage(screenVideo, x, y, width, height);
          };

          drawComposite();
          const compositeStream = compositeCanvas.captureStream(30);

          // Update broadcaster
          if (broadcaster.getVideoInputDevice("screen")) {
            await broadcaster.removeVideoInputDevice("screen");
          }
          await broadcaster.addVideoInputDevice(compositeStream, "screen", {
            index: 0,
          });

          // Update preview
          const container = parent.querySelector("div");
          if (container) {
            const previewVideo = container.querySelector("video");
            if (previewVideo) {
              previewVideo.srcObject = compositeStream;
            }
          }

          // Set up continuous redraw
          let animationFrameId: number;
          const updateFrame = () => {
            drawComposite();
            screenUpdateIntervalRef.current = window.setTimeout(() => {
              animationFrameId = requestAnimationFrame(updateFrame);
            }, 50);
          };
          animationFrameId = requestAnimationFrame(updateFrame);

          // Clean up on screen end
          const originalOnEnded = screenTrack.onended;
          screenTrack.onended = async () => {
            if (animationFrameId) {
              cancelAnimationFrame(animationFrameId);
            }
            if (screenUpdateIntervalRef.current) {
              clearTimeout(screenUpdateIntervalRef.current);
              screenUpdateIntervalRef.current = null;
            }
            if (typeof originalOnEnded === "function") {
              originalOnEnded.call(screenTrack, new Event("ended"));
            }
          };
        }

        setStream(null);
        setVideoTrack(null);
        setCameraEnabled(false);
      } else {
        // Enable camera with screen share
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: getVideoConstraints(),
          audio: false,
        });
        const newVideoTrack = mediaStream.getVideoTracks()[0];
        const newStream = new MediaStream([newVideoTrack]);

        if (screenStream) {
          const screenTrack = screenStream.getVideoTracks()[0];
          const screenVideo = document.createElement("video");
          const cameraVideo = document.createElement("video");

          screenVideo.srcObject = new MediaStream([screenTrack]);
          cameraVideo.srcObject = newStream;

          screenVideo.autoplay = true;
          screenVideo.playsInline = true;
          screenVideo.muted = true;
          cameraVideo.autoplay = true;
          cameraVideo.playsInline = true;
          cameraVideo.muted = true;

          await Promise.all([
            new Promise<void>((resolve) => {
              screenVideo.onloadedmetadata = () =>
                screenVideo.play().then(resolve);
            }),
            new Promise<void>((resolve) => {
              cameraVideo.onloadedmetadata = () =>
                cameraVideo.play().then(resolve);
            }),
          ]);

          const compositeCanvas = document.createElement("canvas");
          compositeCanvas.width = 1920;
          compositeCanvas.height = 1080;
          const ctx = compositeCanvas.getContext("2d");
          if (!ctx) return;

          const drawComposite = () => {
            if (document.hidden) return;
            ctx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);
            ctx.fillStyle = "#000000";
            ctx.fillRect(0, 0, compositeCanvas.width, compositeCanvas.height);

            // Draw screen
            const scale = Math.min(
              compositeCanvas.width / screenVideo.videoWidth,
              compositeCanvas.height / screenVideo.videoHeight
            );
            const width = screenVideo.videoWidth * scale;
            const height = screenVideo.videoHeight * scale;
            const x = (compositeCanvas.width - width) / 2;
            const y = (compositeCanvas.height - height) / 2;
            ctx.drawImage(screenVideo, x, y, width, height);

            // Draw camera PIP
            const pipWidth = Math.floor(compositeCanvas.width * 0.15);
            const pipHeight = Math.floor(pipWidth * (9 / 16));
            const pipMargin = Math.floor(compositeCanvas.width * 0.02);
            const pipX = compositeCanvas.width - pipWidth - pipMargin;
            const pipY = compositeCanvas.height - pipHeight - pipMargin;

            if (isFrontCamera(newVideoTrack.label)) {
              ctx.save();
              ctx.translate(pipX + pipWidth, pipY);
              ctx.scale(-1, 1);
              ctx.drawImage(cameraVideo, 0, 0, pipWidth, pipHeight);
              ctx.restore();
            } else {
              ctx.drawImage(cameraVideo, pipX, pipY, pipWidth, pipHeight);
            }
          };

          drawComposite();
          const compositeStream = compositeCanvas.captureStream(30);

          // Update broadcaster
          if (broadcaster.getVideoInputDevice("screen")) {
            await broadcaster.removeVideoInputDevice("screen");
          }
          await broadcaster.addVideoInputDevice(compositeStream, "screen", {
            index: 0,
          });

          // Update preview
          const container = parent.querySelector("div");
          if (container) {
            const previewVideo = container.querySelector("video");
            if (previewVideo) {
              previewVideo.srcObject = compositeStream;
            }
          }

          // Set up continuous redraw
          let animationFrameId: number;
          const updateFrame = () => {
            drawComposite();
            screenUpdateIntervalRef.current = window.setTimeout(() => {
              animationFrameId = requestAnimationFrame(updateFrame);
            }, 50);
          };
          animationFrameId = requestAnimationFrame(updateFrame);

          // Clean up on screen end
          const originalOnEnded = screenTrack.onended;
          screenTrack.onended = async () => {
            if (animationFrameId) {
              cancelAnimationFrame(animationFrameId);
            }
            if (screenUpdateIntervalRef.current) {
              clearTimeout(screenUpdateIntervalRef.current);
              screenUpdateIntervalRef.current = null;
            }
            if (typeof originalOnEnded === "function") {
              originalOnEnded.call(screenTrack, new Event("ended"));
            }
          };
        }

        setStream(newStream);
        setVideoTrack(newVideoTrack);
        setCameraEnabled(true);
      }
    } catch (error) {
      console.error("Error toggling camera during screen share:", error);
      toast({
        title: "Error",
        description: "Failed to toggle camera during screen share",
        variant: "destructive",
        showIcon: true,
      });
    } finally {
      setUpdating(false);
    }
  }, [
    broadcaster,
    videoRef,
    cameraEnabled,
    stream,
    videoTrack,
    screenStream,
    getVideoConstraints,
    isFrontCamera,
  ]);

  //Start and Stop Camera
  const toggleCamera = useCallback(async () => {
    if (screenSharing) {
      await toggleCameraScreenShare();
    } else {
      try {
        setUpdating(true);
        if (hasVideoPermission) {
          if (cameraEnabled) {
            await broadcaster.disableVideo();
            await broadcaster.removeVideoInputDevice("camera");
            if (videoTrack) {
              videoTrack.stop();
              videoTrack.enabled = false;
            }
            if (stream) {
              stream.getVideoTracks().forEach((track) => {
                track.stop();
                track.enabled = false;
              });
            }
            if (videoRef.current) {
              const ctx = videoRef.current.getContext("2d");
              if (ctx) {
                ctx.clearRect(
                  0,
                  0,
                  videoRef.current.width,
                  videoRef.current.height
                );
              }
            }
            setCameraEnabled(false);
          } else {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
              video: getVideoConstraints(),
              audio: false,
            });
            const newVideoTrack = mediaStream.getVideoTracks()[0];
            if (broadcaster) {
              const isBack = !isFrontCamera(newVideoTrack.label);
              if (!isBack) {
                const mirroredStream = await createMirroredStreamComposite(
                  mediaStream
                );
                await broadcaster.addVideoInputDevice(
                  mirroredStream,
                  "camera",
                  {
                    index: 0,
                  }
                );
              } else {
                await broadcaster.addVideoInputDevice(
                  new MediaStream([newVideoTrack]),
                  "camera",
                  { index: 0 }
                );
              }
              const newStream = new MediaStream([newVideoTrack]);
              if (stream?.getAudioTracks()[0])
                newStream.addTrack(stream.getAudioTracks()[0]);
              setStream(newStream);
              setVideoTrack(newVideoTrack);
              await broadcaster.enableVideo();
            }
            setCameraEnabled(true);
          }
        } else {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            video: getVideoConstraints(),
            audio: false,
          });
          const newVideoTrack = mediaStream.getVideoTracks()[0];
          if (broadcaster) {
            const isBack = !isFrontCamera(newVideoTrack.label);
            if (!isBack) {
              const mirroredStream = await createMirroredStreamComposite(
                mediaStream
              );
              await broadcaster.addVideoInputDevice(mirroredStream, "camera", {
                index: 0,
              });
            } else {
              await broadcaster.addVideoInputDevice(
                new MediaStream([newVideoTrack]),
                "camera",
                { index: 0 }
              );
            }
            const newStream = new MediaStream([newVideoTrack]);
            if (stream?.getAudioTracks()[0])
              newStream.addTrack(stream.getAudioTracks()[0]);
            setStream(newStream);
            setVideoTrack(newVideoTrack);
            setCameraEnabled(true);
            setHasVideoPermission(true);
            await broadcaster.enableVideo();
          }
        }
      } catch (error) {
        console.error("Error toggling camera:", error);
        toast({
          title: "Camera Error",
          description: "Failed to access camera",
          variant: "destructive",
          showIcon: true,
        });
        setCameraEnabled(false);
      } finally {
        setUpdating(false);
      }
    }
  }, [
    broadcaster,
    cameraEnabled,
    videoTrack,
    stream,
    hasVideoPermission,
    screenSharing,
  ]);

  //Start and Stop Microphone
  const toggleMicrophone = useCallback(async () => {
    try {
      setUpdating(true);
      if (hasAudioPermission) {
        if (micOn) {
          await broadcaster.disableAudio();
          await broadcaster.removeAudioInputDevice("microphone");
          if (stream) {
            stream.getAudioTracks().forEach((track) => {
              track.stop();
              track.enabled = false;
            });
          }
          setMicOn(false);
        } else {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
            video: false,
          });
          const newAudioTrack = mediaStream.getAudioTracks()[0];
          if (broadcaster) {
            await broadcaster.addAudioInputDevice(
              new MediaStream([newAudioTrack]),
              "microphone"
            );
            const newStream = new MediaStream([newAudioTrack]);
            const existingVideoTrack = stream?.getVideoTracks()[0];
            if (existingVideoTrack) newStream.addTrack(existingVideoTrack);
            setStream(newStream);
            await broadcaster.enableAudio();
          }
          setMicOn(true);
        }
      } else {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
        const newAudioTrack = mediaStream.getAudioTracks()[0];
        if (broadcaster) {
          try {
            await broadcaster.removeAudioInputDevice("microphone");
          } catch (e) {
            console.log("No existing microphone to remove");
          }
          await broadcaster.addAudioInputDevice(
            new MediaStream([newAudioTrack]),
            "microphone"
          );
          const newStream = new MediaStream([newAudioTrack]);
          const existingVideoTrack = stream?.getVideoTracks()[0];
          if (existingVideoTrack) newStream.addTrack(existingVideoTrack);
          setStream(newStream);
          setMicOn(true);
          setHasAudioPermission(true);
          await broadcaster.enableAudio();
        }
      }
    } catch (error) {
      console.error("Error toggling camera:", error);
      toast({
        title: "Camera Error",
        description: "Failed to access camera",
        variant: "destructive",
        showIcon: true,
      });
      setMicOn(false);
    } finally {
      setUpdating(false);
    }
  }, [broadcaster, micOn, stream, hasAudioPermission]);

  //Camera Flip
  const handleCameraFlip = useCallback(async () => {
    if (!broadcaster || cameras.length < 2) return;
    try {
      setUpdating(true);
      const nextCameraIndex = (currentCameraIndex + 1) % cameras.length;
      const nextCamera = cameras[nextCameraIndex];
      const isNextBack = !isFrontCamera(nextCamera.label);
      if (stream) stream.getVideoTracks().forEach((track) => track.stop());
      const audioTrack = stream?.getAudioTracks()[0];
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: nextCamera.deviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          aspectRatio: 16 / 9,
        },
        audio: false,
      });
      const newVideoTrack = newStream.getVideoTracks()[0];
      if (!isNextBack) {
        const mirroredStream = await createMirroredStreamComposite(newStream);
        await broadcaster.removeVideoInputDevice("camera");
        await broadcaster.addVideoInputDevice(mirroredStream, "camera", {
          index: 0,
        });
      } else {
        await broadcaster.removeVideoInputDevice("camera");
        await broadcaster.addVideoInputDevice(
          new MediaStream([newVideoTrack]),
          "camera",
          { index: 0 }
        );
      }
      const combinedStream = new MediaStream([newVideoTrack]);
      if (audioTrack) combinedStream.addTrack(audioTrack);
      setStream(combinedStream);
      setVideoTrack(newVideoTrack);
      setCurrentCameraIndex(nextCameraIndex);
    } catch (error) {
      console.error("Error flipping camera:", error);
      toast({
        title: "Error",
        description: "Failed to flip camera",
        variant: "destructive",
        showIcon: true,
      });
    } finally {
      setUpdating(false);
    }
  }, [broadcaster, cameras, currentCameraIndex, stream, setUpdating]);

  //Start and Stop Screen Sharing

  const cleanupScreenShare = async () => {
    // Clear the composite drawing interval if set
    if (screenUpdateIntervalRef.current) {
      clearTimeout(screenUpdateIntervalRef.current);
      screenUpdateIntervalRef.current = null;
    }

    if (videoTrack) {
      videoTrack.stop();
      videoTrack.enabled = false;
    }
    if (stream) {
      stream.getVideoTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
    }
    // Stop the screen share tracks
    if (screenStream) {
      screenStream.getTracks().forEach((track) => track.stop());
    }
    if (videoRef.current) {
      const ctx = videoRef.current.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, videoRef.current.width, videoRef.current.height);
      }
    }

    // Clear state and remove devices from broadcaster
    setScreenStream(null);
    setScreenSharing(false);

    if (broadcaster.getVideoInputDevice("screen")) {
      await broadcaster.removeVideoInputDevice("screen");
    }
    // Optionally, re-enable the camera if needed
    if (hasVideoPermission && cameraEnabled) {
      if (broadcaster.getVideoInputDevice("camera")) {
        await broadcaster.removeVideoInputDevice("camera");
      }
      try {
        const newMediaStream = await navigator.mediaDevices.getUserMedia({
          video: getVideoConstraints(),
          audio: false,
        });
        const newVideoTrack = newMediaStream.getVideoTracks()[0];
        const mirroredStream = await createMirroredStreamComposite(
          newMediaStream
        );
        await broadcaster.addVideoInputDevice(mirroredStream, "camera", {
          index: 0,
        });
        setStream(newMediaStream);
        setVideoTrack(newVideoTrack);
        await broadcaster.enableVideo();
        setCameraEnabled(true);
      } catch (error) {
        console.error("Error re-enabling camera:", error);
        setCameraEnabled(false);
        toast({
          title: "Error",
          description: "Failed to restore camera after screen sharing",
          variant: "destructive",
          showIcon: true,
        });
      }
    }
  };

  const toggleScreenShare = useCallback(async () => {
    if (!broadcaster || !videoRef.current) return;
    try {
      if (screenSharing) {
        // Use the stored parent reference and cast it to Element
        const parent = (parentNodeRef.current ||
          videoRef.current.parentNode) as Element;
        if (parent) {
          const container = parent.querySelector("div");
          if (container) {
            // Replace the container with the original canvas
            parent.replaceChild(videoRef.current, container);
          }
        }
        await cleanupScreenShare();
      } else {
        // Store the parent reference before starting screen share
        parentNodeRef.current = videoRef.current.parentNode;

        const newScreenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
            displaySurface: "browser",
          } as MediaTrackConstraints,
          audio: false,
        });

        setScreenStream(newScreenStream);
        const compositeCanvas = document.createElement("canvas");

        // Get the actual screen dimensions from the video track
        const screenTrack = newScreenStream.getVideoTracks()[0];
        const screenSettings = screenTrack.getSettings();

        // Set fixed dimensions for the canvas
        compositeCanvas.width = 1920;
        compositeCanvas.height = 1080;

        const ctx = compositeCanvas.getContext("2d");
        if (!ctx || !videoRef.current) return;

        const screenVideo = document.createElement("video");
        screenVideo.srcObject = newScreenStream;
        screenVideo.autoplay = true;
        screenVideo.playsInline = true;
        screenVideo.muted = true;

        let cameraVideo: HTMLVideoElement | null = null;
        if (cameraEnabled && stream) {
          cameraVideo = document.createElement("video");
          cameraVideo.srcObject = stream;
          cameraVideo.autoplay = true;
          cameraVideo.playsInline = true;
          cameraVideo.muted = true;
        }

        await Promise.all([
          new Promise<void>((resolve) => {
            screenVideo.onloadedmetadata = () =>
              screenVideo.play().then(resolve);
          }),
          cameraVideo
            ? new Promise<void>((resolve) => {
                cameraVideo!.onloadedmetadata = () =>
                  cameraVideo!.play().then(resolve);
              })
            : Promise.resolve(),
        ]);

        const drawComposite = () => {
          if (!ctx || !screenVideo) return;

          ctx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);
          ctx.fillStyle = "#000000";
          ctx.fillRect(0, 0, compositeCanvas.width, compositeCanvas.height);

          // Calculate dimensions to maintain aspect ratio
          const videoAspect = screenVideo.videoWidth / screenVideo.videoHeight;
          const canvasAspect = compositeCanvas.width / compositeCanvas.height;

          let drawWidth = compositeCanvas.width;
          let drawHeight = compositeCanvas.height;
          let x = 0;
          let y = 0;

          if (videoAspect > canvasAspect) {
            // Video is wider than canvas
            drawHeight = drawWidth / videoAspect;
            y = (compositeCanvas.height - drawHeight) / 2;
          } else {
            // Video is taller than canvas
            drawWidth = drawHeight * videoAspect;
            x = (compositeCanvas.width - drawWidth) / 2;
          }

          // Draw screen with proper aspect ratio
          ctx.drawImage(screenVideo, x, y, drawWidth, drawHeight);

          // Draw camera PIP if enabled
          if (cameraVideo && cameraEnabled) {
            const pipWidth = Math.floor(compositeCanvas.width * 0.15);
            const pipHeight = Math.floor(pipWidth * (9 / 16));
            const pipMargin = Math.floor(compositeCanvas.width * 0.02);
            const pipX = compositeCanvas.width - pipWidth - pipMargin;
            const pipY = compositeCanvas.height - pipHeight - pipMargin;

            if (isFrontCamera(stream?.getVideoTracks()[0]?.label || "")) {
              ctx.save();
              ctx.translate(pipX + pipWidth, pipY);
              ctx.scale(-1, 1);
              ctx.drawImage(cameraVideo, 0, 0, pipWidth, pipHeight);
              ctx.restore();
            } else {
              ctx.drawImage(cameraVideo, pipX, pipY, pipWidth, pipHeight);
            }
          }
        };

        drawComposite();
        const compositeStream = compositeCanvas.captureStream(30);
        await broadcaster.addVideoInputDevice(compositeStream, "screen", {
          index: 0,
        });

        if (videoRef.current) {
          const parent = (parentNodeRef.current ||
            videoRef.current.parentNode) as Element;
          if (parent) {
            const container = document.createElement("div");
            container.style.position = "relative";
            container.style.width = "100%";
            container.style.height = "100%";

            const previewVideo = document.createElement("video");
            previewVideo.srcObject = compositeStream;
            previewVideo.autoplay = true;
            previewVideo.playsInline = true;
            previewVideo.muted = true;
            previewVideo.style.width = "100%";
            previewVideo.style.height = "100%";
            previewVideo.style.objectFit = "contain";

            container.appendChild(previewVideo);
            parent.replaceChild(container, videoRef.current);

            screenUpdateIntervalRef.current = window.setInterval(
              drawComposite,
              1000 / 60
            );

            // Handle screen share ending (both manual and system UI)
            newScreenStream.getVideoTracks()[0].onended = async () => {
              try {
                if (parent) {
                  const currentContainer = parent.querySelector("div");
                  if (currentContainer) {
                    // @ts-ignore
                    parent.replaceChild(videoRef.current, currentContainer);
                  }
                }
                await cleanupScreenShare();
              } catch (error) {
                console.error("Error handling screen share end:", error);
                toast({
                  title: "Error",
                  description: "Failed to end screen sharing",
                  variant: "destructive",
                  showIcon: true,
                });
              }
            };
          }
        }
        setScreenSharing(true);
      }
    } catch (error) {
      console.error("Error toggling screen share:", error);
      setScreenSharing(false);
      toast({
        title: "Error",
        description: "Failed to toggle screen sharing",
        variant: "destructive",
        showIcon: true,
      });
    }
  }, [
    broadcaster,
    screenSharing,
    videoRef,
    videoTrack,
    stream,
    cameraEnabled,
    hasVideoPermission,
    screenStream,
    getVideoConstraints,
    isFrontCamera,
  ]);

  /* ============================================
       Permission Change Handling
    ============================================ */
  const handlePermissionChange = useCallback(
    async (permissionName: "camera" | "microphone") => {
      try {
        // First try to get the permission state via the Permissions API.
        const permission = await navigator.permissions.query({
          name: permissionName as PermissionName,
        });
        console.log(`${permissionName} permission state:`, permission.state);
        if (permission.state === "denied" || permission.state === "prompt") {
          // If explicitly denied, update state and disable the input.
          if (permissionName === "camera" && cameraEnabled) {
            if (broadcaster) {
              await broadcaster.disableVideo();
              try {
                await broadcaster.removeVideoInputDevice("camera");
              } catch (e) {
                console.log("No camera device to remove");
              }
              if (videoTrack) {
                videoTrack.stop();
                videoTrack.enabled = false;
              }
              if (stream) {
                stream.getVideoTracks().forEach((track) => {
                  track.stop();
                  track.enabled = false;
                });
              }
              if (videoRef.current) {
                const ctx = videoRef.current.getContext("2d");
                ctx?.clearRect(
                  0,
                  0,
                  videoRef.current.width,
                  videoRef.current.height
                );
              }
            }
            setCameraEnabled(false);
            setHasVideoPermission(false);
            toast({
              title: "Camera Disabled",
              description: "Camera permission was revoked",
              variant: "destructive",
              showIcon: true,
            });
          } else if (permissionName === "microphone" && micOn) {
            if (broadcaster) await broadcaster.disableAudio();
            setMicOn(false);
            setHasAudioPermission(false);
            toast({
              title: "Microphone Disabled",
              description: "Microphone permission was revoked",
              variant: "destructive",
              showIcon: true,
            });
          }
        }
      } catch (error) {
        console.error(
          `Error handling ${permissionName} permission change:`,
          error
        );
      }
    },
    [broadcaster, cameraEnabled, micOn, videoTrack, checkMediaPermissions]
  );

  useEffect(() => {
    let cleanupFn: () => void = () => {};
    let intervalId: number | undefined;

    const setupPermissionListeners = async () => {
      try {
        // Use Permissions API listeners for camera and microphone.
        const cameraPermission = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        const micPermission = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });

        // Define stable listener functions.
        const cameraListener = () => handlePermissionChange("camera");
        const micListener = () => handlePermissionChange("microphone");

        cameraPermission.addEventListener("change", cameraListener);
        micPermission.addEventListener("change", micListener);

        cleanupFn = () => {
          cameraPermission.removeEventListener("change", cameraListener);
          micPermission.removeEventListener("change", micListener);
        };
      } catch (error) {
        console.warn(
          "Permissions API not fully supported, falling back to periodic checks."
        );
        // Fallback: periodically check available devices.
        intervalId = window.setInterval(async () => {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const hasVideo = devices.some(
            (device) => device.kind === "videoinput" && device.label
          );
          const hasAudio = devices.some(
            (device) => device.kind === "audioinput" && device.label
          );
          if (!hasVideo && cameraEnabled) {
            await handlePermissionChange("camera");
          }
          if (!hasAudio && micOn) {
            await handlePermissionChange("microphone");
          }
        }, 1000);
        cleanupFn = () => {
          if (intervalId) clearInterval(intervalId);
        };
      }

      // Additionally, add a device change listener to catch any hardware changes.
      const deviceChangeHandler = async () => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(
          (device) => device.kind === "videoinput"
        );
        const hasMic = devices.some((device) => device.kind === "audioinput");

        if (!hasCamera && cameraEnabled) {
          await handlePermissionChange("camera");
        }
        if (!hasMic && micOn) {
          await handlePermissionChange("microphone");
        }
      };

      navigator.mediaDevices.addEventListener(
        "devicechange",
        deviceChangeHandler
      );
      const originalCleanup = cleanupFn;
      cleanupFn = () => {
        originalCleanup();
        navigator.mediaDevices.removeEventListener(
          "devicechange",
          deviceChangeHandler
        );
      };
    };

    setupPermissionListeners();
    return cleanupFn;
  }, [handlePermissionChange, cameraEnabled, micOn]);

  // Auto-stop broadcast if showAlertAction becomes true.
  useEffect(() => {
    if (broadcaster && showAlertAction) {
      onLeaveClean();
      setShowAlertAction(false);
    }
  }, [broadcaster, showAlertAction]);

  const onLeaveClean = async () => {
    cleanupBroadcaster();
    cleanupStreams();
    // Reset states
    setMicOn(false);
    setCameraEnabled(false);
    setVideoTrack(null);
    setShowAlertOnRouteChange(false);
    setSelectedData(null);

    // Handle Safari-specific cleanup
    if (
      /Safari/.test(navigator.userAgent) &&
      !/Chrome/.test(navigator.userAgent)
    ) {
      try {
        const emptyStream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: false,
        });
        if (emptyStream) {
          emptyStream?.getTracks().forEach((track) => track.stop());
        }
      } catch (err) {
        console.warn("Safari: Unable to release camera:", err);
      }
    }
  };

  /* ============================================
       Return Hook API
    ============================================ */
  return {
    micOn,
    cameraEnabled,
    screenSharing,
    streamStatus,
    videoTrack,
    stream,
    broadcaster,
    cameras,
    currentCameraIndex,
    hasVideoPermission,
    hasAudioPermission,
    getCameras,
    startBroadcast,
    stopBroadcast,
    toggleCamera,
    handleCameraFlip,
    toggleMicrophone,
    toggleScreenShare,
    loadingProgress,
    loadingMessage,
    onLeaveClean,
  };
};
